import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useCompetencies,
  useCreateCompetency,
  useUpdateCompetency,
  useDeleteCompetency,
  type Competency,
} from '@/hooks/useCompetencies';

export default function CompetenciesTab() {
  const { data: competencies = [], isLoading } = useCompetencies();
  const createMut = useCreateCompetency();
  const updateMut = useUpdateCompetency();
  const deleteMut = useDeleteCompetency();

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ open: boolean; item?: Competency }>({ open: false });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [form, setForm] = useState({ name: '', sort_order: '0' });

  const filtered = competencies.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()));

  const openDialog = (item?: Competency) => {
    setForm(item ? { name: item.name, sort_order: String(item.sort_order) } : { name: '', sort_order: String(competencies.length + 1) });
    setDialog({ open: true, item });
  };

  const save = async () => {
    if (!form.name) return;
    try {
      if (dialog.item) {
        await updateMut.mutateAsync({ id: dialog.item.id, name: form.name, sort_order: Number(form.sort_order) || 0 });
      } else {
        await createMut.mutateAsync({ name: form.name, sort_order: Number(form.sort_order) || 0 });
      }
      setDialog({ open: false });
      toast({ title: dialog.item ? 'Competencia actualizada' : 'Competencia agregada' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteMut.mutateAsync(deleteDialog.id);
      setDeleteDialog({ open: false, id: '' });
      toast({ title: 'Competencia eliminada' });
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar competencia…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button onClick={() => openDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} de {competencies.length} competencias</p>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Orden</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead className="w-24">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(comp => (
              <TableRow key={comp.id}>
                <TableCell>{comp.sort_order}</TableCell>
                <TableCell className="font-medium">{comp.name}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(comp)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, id: comp.id })}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog.item ? 'Editar Competencia' : 'Agregar Competencia'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Machine Learning" /></div>
            <div><Label>Orden</Label><Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false })}>Cancelar</Button>
            <Button onClick={save} disabled={!form.name}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta competencia?</AlertDialogTitle>
            <AlertDialogDescription>Las rutas que la usen dejarán de mostrarla.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
